import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {RECYCLED_RESOURCE_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import type {RecycleBinLocationParamsType} from 'app/recycle-bin/types';
import type {
  ListResponseType,
  RecycledResourceSerializedType,
  UserJobSerializedType,
} from 'sweet-types';

export const RECYCLED_TAGS = {
  TAG: 'recycle',
};

type RestoreDeleteRecycledResourceParams = {
  hashid: string;
};

type BulkRestoreDeleteResourcesParams = {
  selectedItems: Array<string>;
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [RECYCLED_TAGS.TAG],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      recycledResources: build.query<
        ListResponseType<RecycledResourceSerializedType>,
        RecycleBinLocationParamsType
      >({
        query: (params = {}) => ({
          url: RECYCLED_RESOURCE_API_URL,
          params: {page: 1, ...params},
        }),
        onQueryStarted: showLoadingBar,
        providesTags: providesTagsByURL(RECYCLED_TAGS.TAG),
      }),
      restoreRecycledResource: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        RestoreDeleteRecycledResourceParams
      >({
        queryFn: (
          {hashid},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${RECYCLED_RESOURCE_API_URL}${hashid}/restore/`,
                    method: 'POST',
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: RECYCLED_TAGS.TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () => gt('Restoring item...'),
            onSuccess: () => [
              gt('Item restored successfully.'),
              {level: 'success'},
            ],
            onError: () =>
              gt("There was an error and we couldn't restore the item."),
          })(args, api);
        },
      }),
      deleteRecycledResource: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        RestoreDeleteRecycledResourceParams
      >({
        queryFn: (
          {hashid},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${RECYCLED_RESOURCE_API_URL}${hashid}/permanently_delete/`,
                    method: 'POST',
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: RECYCLED_TAGS.TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () => gt('Permanently deleting item...'),
            onSuccess: () => [
              gt('This item was permanently deleted.'),
              {level: 'success'},
            ],
            onError: () =>
              gt("There was an error and we couldn't delete this item."),
          })(args, api);
        },
      }),
      bulkRestoreRecycledResources: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        BulkRestoreDeleteResourcesParams
      >({
        queryFn: (
          {selectedItems},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${RECYCLED_RESOURCE_API_URL}bulk_restore/`,
                    method: 'POST',
                    body: {
                      hashids: selectedItems,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: RECYCLED_TAGS.TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () => gt('The selected items are being restored...'),
            onSuccess: () => [
              gt('All of the selected items were restored.'),
              {level: 'success'},
            ],
            onError: () =>
              gt(
                "There was an error and we couldn't restore the selected items."
              ),
          })(args, api);
        },
      }),
      bulkDeleteRecycledResources: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        BulkRestoreDeleteResourcesParams
      >({
        queryFn: (
          {selectedItems},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${RECYCLED_RESOURCE_API_URL}bulk_delete/`,
                    method: 'POST',
                    body: {
                      hashids: selectedItems,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: RECYCLED_TAGS.TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () =>
              gt('The selected items are being permanently deleted...'),
            onSuccess: () => [
              gt('The selected items were permanently deleted.'),
              {level: 'success'},
            ],
            onError: () =>
              gt(
                "There was an error and we couldn't delete the selected items."
              ),
          })(args, api);
        },
      }),
    }),
  });

export const {
  useRecycledResourcesQuery: useRecycledResources,
  useRestoreRecycledResourceMutation: useRestoreRecycledResource,
  useDeleteRecycledResourceMutation: useDeleteRecycledResource,
  useBulkRestoreRecycledResourcesMutation: useBulkRestoreRecycledResources,
  useBulkDeleteRecycledResourcesMutation: useBulkDeleteRecycledResources,
} = extendedAPI;
